import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProfileData } from '@/hooks/useProfileData';
import FlyerTemplate from '@/components/qr-flyer/FlyerTemplate';
import { FlyerConfigFullOptions } from '@/types/flyerTypes';

const QrFlyerPrint: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { 
    profileData, 
    isLoading, 
    sportStats,
    combineScores,
  } = useProfileData();

  const [flyerConfig] = useState<FlyerConfigFullOptions>({
    // Required fields
    showCombineStats: true,
    showSportStats: true,
    showAchievements: true,
    showHighlightFilm: true,
    showRecruitingVideos: true,
    showRecommendations: true,
    showPreferences: true,
    
    // All other fields
    showGPA: true,
    showSAT: true,
    showACT: true,
    showMajor: true,
    showSchool: true,
    showPhone: true,
    showEmail: true,
    showVideos: true,
    showCoachRecommendations: true,
    showRecruitingPreferences: true,
    showEvents: true,
    showPress: true,
    showOffers: true,
    showMedia: true,
    showHighSchool: true,
    showHometown: true,
    showSportSpecificStats: true,
    showAthleticAchievements: true,
    showSATACT: true,
    showAcademicAchievements: true,
    showUpcomingEvents: true,
    showVideoHighlights: true,
    showCoachQuotes: true,
    showPressCoverage: true,
    showPastEvents: true,
    showExperience: true,
    showPressClippings: true,
    theme: 'default',
    showQr: true,
    showBio: true,
    showSocialMedia: true,
    showStats: true,
    accentColor: '#002E62',
    secondaryColor: '#BC8D09',
    qrTargetUrl: profileData?.username ? `https://recruitlook.com/profile/${profileData.username}` : '',
    includeContactInfo: true,
    showSpecificSocialMedia: {
      instagram: true,
      twitter: true,
      tiktok: true,
      youtube: true,
      hudl: true
    },
    showGradYear: true,
    showSport: true,
    showPosition: true,
    showSecondaryPosition: true,
    showHeight: true,
    showWeight: true,
    showDOB: true,
    showFullName: true,
    showAvatar: true
  });

  // Auto-trigger print when loaded
  useEffect(() => {
    if (!isLoading && profileData) {
      // Small delay to ensure rendering is complete
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [isLoading, profileData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading flyer...</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.3in;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        body {
          margin: 0;
          padding: 0.5rem;
        }
        
        .flyer-content {
          max-width: 8.5in;
          margin: 0 auto;
        }
        
        .flyer-content > * {
          margin-bottom: 0.5rem;
        }
        
        .flyer-content h1,
        .flyer-content h2,
        .flyer-content h3 {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
        }
      `}</style>
      
      <div className="flyer-content">
        <FlyerTemplate
          profileData={profileData}
          athleteStats={{
            combineScores: combineScores || [],
            sportStats: sportStats || []
          }}
          config={flyerConfig}
        />
      </div>
    </div>
  );
};

export default QrFlyerPrint;
